import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { CircularProgress } from '@mui/material'
import React from 'react'
import { Link } from 'react-router-dom'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { CheckIcon } from '../../../template/kartera/icons'
import { formatCount } from '../common/policy-import-common'
import { PDF_ACCEPT, TROUBLESHOOTING, usePolicyImportPdfModel } from './policy-import-pdf-model'
import type { PdfFileState } from './policy-import-pdf-model'

const primaryBtnClass = 'inline-flex h-11 items-center gap-2 rounded-xl border-0 bg-k-primary px-[18px] text-sm font-bold !text-white hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-40'
const outlineBtnClass = 'inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40'

const FileStateIcon = ({ state }: { state: PdfFileState }) => {
    if (state === 'working') return <CircularProgress size={20} sx={{ color: 'rgb(var(--k-primary))' }} />
    if (state === 'done') return <CheckCircleIcon className="text-k-ok" style={{ fontSize: 22 }} />
    if (state === 'error') return <ErrorOutlineIcon className="text-k-danger" style={{ fontSize: 22 }} />
    return <PictureAsPdfOutlinedIcon className="text-tone-pink-ink" style={{ fontSize: 22 }} />
}

const fileTone: Record<PdfFileState, string> = {
    hold: 'border-k-line',
    working: 'border-k-primary bg-k-accent-soft/40',
    done: 'border-k-ok-line bg-k-ok-sunken',
    error: 'border-k-danger-soft bg-k-danger-soft/40',
}

const PolicyImportPdf = () => {
    const model = usePolicyImportPdfModel()

    return (
        <KarteraAdminTemplate loading={false} title="Importar carátulas">
            <div className="mx-auto flex w-full max-w-[880px] flex-col gap-[18px]">
                <KarteraPageHeader
                    title="Importar carátulas (PDF)"
                    description="Suelta las carátulas originales y crearemos las pólizas en tu cartera, una por una."
                    backTo={model.backTo}
                    backLabel="Importar pólizas"
                />

                <label
                    {...model.drag}
                    className={`flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors ${model.working
                        ? 'cursor-not-allowed border-k-line bg-k-sunken opacity-60'
                        : model.dragging
                            ? 'cursor-copy border-k-primary bg-k-accent-soft'
                            : 'cursor-pointer border-k-line-strong bg-k-surface hover:border-k-primary hover:bg-k-hover'}`}
                >
                    <span className="flex h-14 w-14 items-center justify-center rounded-[16px] bg-tone-pink text-tone-pink-ink">
                        <UploadFileOutlinedIcon style={{ fontSize: 30 }} />
                    </span>
                    <span className="text-base font-extrabold text-k-ink">Suelta aquí las carátulas de tus pólizas</span>
                    <span className="text-sm text-k-muted">o da clic para seleccionarlas desde tus archivos · solo PDF</span>
                    <input
                        type="file"
                        multiple
                        accept={PDF_ACCEPT}
                        className="sr-only"
                        disabled={model.working}
                        onChange={(event) => { model.addFiles(event.target.files); event.target.value = '' }}
                    />
                </label>

                {model.rejected > 0 && (
                    <p className="m-0 text-sm text-k-danger-ink">
                        {model.rejected === 1 ? 'Se descartó 1 archivo que no es PDF.' : `Se descartaron ${model.rejected} archivos que no son PDF.`}
                    </p>
                )}

                {model.files.length > 0 && (
                    <section ref={model.listRef} className="k-card scroll-mt-6">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <h2 className="k-card-title">Carátulas</h2>
                            <span className="text-xs font-semibold tabular-nums text-k-muted">
                                {formatCount(model.files.length)} archivos · {formatCount(model.done)} importadas · {formatCount(model.failed)} con error · {formatCount(model.pending)} pendientes
                            </span>
                        </div>
                        <ul className="m-0 flex list-none flex-col gap-2 p-0">
                            {model.files.map((item) => (
                                <li key={item.key} className={`flex items-start gap-3 rounded-[14px] border px-4 py-3 ${fileTone[item.state]}`}>
                                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center"><FileStateIcon state={item.state} /></span>
                                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                        <span className="truncate text-sm font-bold text-k-ink" title={item.file.name}>{item.file.name}</span>
                                        {item.state === 'working' && <span className="text-xs text-k-muted">Leyendo carátula…</span>}
                                        {item.state === 'error' && item.message && <span className="text-xs text-k-danger-ink">{item.message}</span>}
                                        {item.state === 'done' && item.poliza && (
                                            <span className="text-xs text-k-ok">
                                                Se creó la póliza {item.poliza.noPoliza} de {item.poliza.agenteClave?.aseguradora?.abreviacion || 'la aseguradora'} ·{' '}
                                                <Link to={model.policyPath(item.poliza.id)} className="font-bold underline">Ver póliza</Link>
                                            </span>
                                        )}
                                    </span>
                                    {item.state === 'hold' && !model.working && (
                                        <button
                                            type="button"
                                            aria-label={`Quitar ${item.file.name}`}
                                            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border-0 bg-transparent text-k-muted hover:bg-k-hover hover:text-k-ink"
                                            onClick={() => model.removeFile(item.key)}
                                        >
                                            <CloseIcon style={{ fontSize: 18 }} />
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                <div className="flex flex-wrap items-center justify-end gap-2.5">
                    <button type="button" className={outlineBtnClass} onClick={model.reset} disabled={model.working || model.files.length === 0}>Reiniciar</button>
                    <button type="button" className={primaryBtnClass} onClick={model.importAll} disabled={model.working || model.pending === 0}>
                        {model.working ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CheckIcon />}
                        {model.working ? 'Importando…' : model.pending > 0 ? `Importar ${formatCount(model.pending)} ${model.pending === 1 ? 'carátula' : 'carátulas'}` : 'Importar'}
                    </button>
                </div>

                <section className="k-card">
                    <h2 className="k-card-title">Posibles errores y soluciones</h2>
                    <ul className="m-0 flex list-none flex-col gap-3 p-0">
                        {TROUBLESHOOTING.map((item) => (
                            <li key={item.id} className="flex flex-col gap-0.5 text-sm">
                                <span className="font-bold text-k-ink">{item.title}</span>
                                <span className="text-k-ink-2">
                                    {item.text}
                                    {item.link && (
                                        <>
                                            {' '}
                                            <Link to={model.agentKeysLocation.pathname} state={model.agentKeysLocation.state} className="font-bold text-k-primary-ink hover:underline">Ir a claves de agente</Link>
                                        </>
                                    )}
                                </span>
                            </li>
                        ))}
                    </ul>
                </section>
            </div>
        </KarteraAdminTemplate>
    )
}

export default PolicyImportPdf
